import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { colors } from '@/styles/theme'
import { tokens } from '@musinsa/layer-tokens/desktop'
import { isNil } from 'lodash-es'
import { HStack, VStack } from '@musinsa/mwms-ds'

interface Props {
  className?: string
  contentClassName?: string
  type?: 'v' | 'h'
  list: {
    content: React.ReactNode
    /** 해당 pane 초기 높이(px). 미지정 시 contentMinHeight 사용 (드래그로 조절 가능) */
    initialHeight?: number
  }[]
  contentMinHeight?: number
}

function ResizableContentsV2({ type = 'v', ...rest }: Props) {
  return type === 'v' ? <DragVertical {...rest} /> : <DragHorizontal {...rest} />
}

function DragVertical({ className, contentClassName, list = [], contentMinHeight = 400 }: Omit<Props, 'type'>) {
  const [tableHeights, setTableHeights] = useState<number[]>(list.map((item) => item.initialHeight ?? contentMinHeight))
  const draggingIndex = useRef<number | null>(null)
  const listRef = useRef<HTMLDivElement[]>([])
  const setListRef = useCallback(
    (el: HTMLDivElement | null, idx: number) => {
      if (el) listRef.current[idx] = el
    },
    [listRef],
  )

  const handleMouseMove = useCallback((event: MouseEvent) => {
    const deltaY = event.movementY

    setTableHeights((prevHeights) => {
      const newHeights = [...prevHeights]
      if (isNil(draggingIndex) || isNil(draggingIndex.current)) return newHeights
      newHeights[draggingIndex.current] = prevHeights[draggingIndex.current] + deltaY
      const isLastBarMoved = draggingIndex.current === list.length - 2
      newHeights[draggingIndex.current + 1] = isLastBarMoved
        ? prevHeights[draggingIndex.current + 1]
        : prevHeights[draggingIndex.current + 1] - deltaY

      return newHeights
    })
  }, [])

  const handleMouseUp = useCallback(() => {
    document.removeEventListener('mousemove', handleMouseMove)
    document.removeEventListener('mouseup', handleMouseUp)
    draggingIndex.current = null
  }, [handleMouseMove])

  const handleMouseDown = useCallback(
    (index: number) => () => {
      draggingIndex.current = index
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    },
    [handleMouseMove, handleMouseUp],
  )

  useEffect(() => {
    if (!listRef) return
    if (!Array.isArray(listRef.current)) return
    setTableHeights(listRef.current?.map((item) => item.offsetHeight))
  }, [listRef])

  return (
    <Container className={className}>
      {list.map((table, index) => {
        const { content } = table

        return (
          <React.Fragment key={`resizable-contents-v2-vertical-${index}`}>
            <VContent
              className={contentClassName}
              ref={(el) => setListRef(el, index)}
              style={tableHeights[index] ? { height: `${tableHeights[index]}px`, overflowY: 'inherit' } : {}}
            >
              {content}
            </VContent>
            {index < list.length - 1 && <DragWrap align="center" justify="center" onMouseDown={handleMouseDown(index)} />}
          </React.Fragment>
        )
      })}
    </Container>
  )
}

const MIN_DRAG_GAP = 10
function DragHorizontal({ className, contentClassName, list = [] }: Omit<Props, 'type'>) {
  const [tableWidths, setTableWidths] = useState<number[]>(Array(list.length).fill(400))
  const draggingIndex = useRef<number | null>(null)
  const listRef = useRef<HTMLDivElement[]>([])

  const handleMouseMove = useCallback((event: MouseEvent) => {
    const deltaX = event.movementX
    setTableWidths((prevWidths) => {
      const newWidths = [...prevWidths]
      if (isNil(draggingIndex) || isNil(draggingIndex.current)) return newWidths
      const newWidthLeft = prevWidths[draggingIndex.current] + deltaX
      const newWidthRight = prevWidths[draggingIndex.current + 1] - deltaX
      if (newWidthLeft > MIN_DRAG_GAP && newWidthRight > MIN_DRAG_GAP) {
        newWidths[draggingIndex.current] = newWidthLeft
        newWidths[draggingIndex.current + 1] = newWidthRight
      }
      return newWidths
    })
  }, [])

  const handleMouseUp = useCallback(() => {
    document.removeEventListener('mousemove', handleMouseMove)
    document.removeEventListener('mouseup', handleMouseUp)
    draggingIndex.current = null
  }, [handleMouseMove])

  const handleMouseDown = useCallback(
    (index: number) => () => {
      draggingIndex.current = index
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    },
    [handleMouseMove, handleMouseUp],
  )

  useEffect(() => {
    if (!Array.isArray(listRef.current)) return
    setTableWidths(listRef.current.map((item) => item.offsetWidth))
  }, [listRef])

  return (
    <HStack className={className} style={{ width: '100%' }}>
      {list.map((item, index) => (
        <React.Fragment key={`resizable-contents-v2-horizontal-${index}`}>
          <HContent
            className={contentClassName}
            ref={(el) => {
              if (el) listRef.current[index] = el
            }}
            style={tableWidths[index] ? { width: `${tableWidths[index]}px` } : {}}
          >
            <HContentScroll>{item.content}</HContentScroll>
          </HContent>
          {index < list.length - 1 && <DragWrap $isHorizontal align="center" justify="center" onMouseDown={handleMouseDown(index)} />}
        </React.Fragment>
      ))}
    </HStack>
  )
}

const Container = styled(VStack)`
  padding-bottom: 24px;
`

const DragWrap = styled(HStack)<{ $isHorizontal?: boolean }>`
  background-color: ${colors.gray200};
  border-top: 1px solid ${tokens.stroke.divider};
  border-bottom: 1px solid ${tokens.stroke.divider};
  margin-top: 16px;
  cursor: ${({ $isHorizontal }) => ($isHorizontal ? 'col-resize' : 'row-resize')};
  min-height: 8px;
  width: 100%;
`

const VContent = styled.div`
  overflow-y: auto;
`

const HContent = styled.div`
  flex-grow: 1;
  position: relative;

  .ag-theme-balham {
    border-radius: 0;
    .ag-root-wrapper.ag-layout-normal {
      border-radius: 0;
    }
  }
`

const HContentScroll = styled.div`
  overflow: auto;
  height: 100%;
`

export default ResizableContentsV2
